// Time off people have asked for and nobody has answered yet.
//
// A request sits here doing nothing until it is decided — it does not block a
// slot and it does not stop a shift. What it does do is show up in the grid as
// a warning, so a week never gets built over an ask that was simply missed.
//
// Declining takes a note. A "no" with no reason is a "no" the person has to
// come and ask about, which is the conversation the request was supposed to be.

import { useState } from 'react';
import { fmtDate } from '../../lib/dates';

function shortDay(iso) {
  return fmtDate(iso, 'dayShort');
}

function span(r) {
  const days = r.startDate === r.endDate ? shortDay(r.startDate) : `${shortDay(r.startDate)} – ${shortDay(r.endDate)}`;
  const count =
    Math.round(
      (new Date(r.endDate + 'T00:00:00Z') - new Date(r.startDate + 'T00:00:00Z')) / 86400000,
    ) + 1;
  return r.allDay ? `${days}${count > 1 ? ` (${count}d)` : ''}` : `${days} · part of the day`;
}

export default function RequestsInbox({ requests = [], busy, error, onDecide, onClose }) {
  const [declining, setDeclining] = useState(null); // request id
  const [note, setNote] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Time off requests"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Time off requests</h2>
            <p className="text-sm text-ink-500">
              {requests.length === 0
                ? 'Nothing waiting.'
                : `${requests.length} waiting on you.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {requests.length === 0 && (
            <p className="text-sm text-ink-400">
              When somebody asks for time off it lands here. Until you answer, it shows on the schedule as a
              warning but blocks nothing.
            </p>
          )}

          {requests.map((r) => (
            <div key={r.id} className="rounded-lg border border-ink-200 overflow-hidden">
              <div className="px-4 py-3">
                <p className="text-sm font-medium text-ink-900">{span(r)}</p>
                <p className="text-sm text-ink-600">{r.name}</p>
                {r.reason && <p className="text-sm text-ink-400 mt-1">{r.reason}</p>}
              </div>

              {declining === r.id ? (
                <div className="px-4 py-3 border-t border-ink-100 bg-ink-50">
                  <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor={`note-${r.id}`}>
                    Why not
                  </label>
                  <input
                    id={`note-${r.id}`}
                    className="input w-full"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="We're short that weekend — can you take the Monday instead?"
                    autoFocus
                  />
                  <p className="text-xs text-ink-400 mt-1">They get this with the decision.</p>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setDeclining(null);
                        setNote('');
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn bg-rose-600 text-white hover:bg-rose-700"
                      disabled={busy}
                      onClick={() => {
                        onDecide(r, 'decline', note.trim() || null);
                        setDeclining(null);
                        setNote('');
                      }}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 border-t border-ink-100 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy}
                    onClick={() => setDeclining(r.id)}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busy}
                    onClick={() => onDecide(r, 'approve', null)}
                  >
                    Approve
                  </button>
                </div>
              )}
            </div>
          ))}

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
